import { useState, useEffect, useRef } from 'react'
import Select from 'react-select'
import { Form, Button, Row, Col } from 'react-bootstrap'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { useDispatch } from 'react-redux'
import { setMessage } from 'src/store/alertMessage/actions'
import { getParty, getCommissionData } from 'src/app/modules/party-entry/auto-vatav/_request'
import { constants } from 'src/utils/const'
import DatePicker from "react-datepicker"
import { useNavigate } from 'react-router-dom';

import {
  fetchCommissionDataAPI,
  fetchPartyDetailsAPI,
  fetchReceiverOptionsAPI,
  fetchSenderOptionsAPI,
  PartyOption,
  submitAutoVatavFormAPI,
} from '../auto-vatav/_request'
import { KTSVG } from 'src/_metronic/helpers'

const NormalEntry: React.FC = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false)
  const today = new Date()
  const [allParties, setAllParties] = useState<PartyOption[]>([])
  const [date] = useState(new Date().toISOString().split('T')[0])
  const [senderParty, setSenderParty] = useState<PartyOption | null>(null)
  const [receiverParty, setReceiverParty] = useState<PartyOption | null>(null)
  const [senderOptions, setSenderOptions] = useState<PartyOption[]>([])
  const [receiverOptions, setReceiverOptions] = useState<PartyOption[]>([])
  const [balances, setBalances] = useState<any>(null)
  const [charCount, setCharCount] = useState(70)
  const [debounceTimeout, setDebounceTimeout] = useState<NodeJS.Timeout | null>(null)
    document.title = `PANEL | Normal Entry`

  const senderSelectRef = useRef<any>(null)
  const receiverSelectRef = useRef<any>(null)
  const amountInputRef = useRef<HTMLInputElement>(null)
  const remarkSelectRef = useRef<HTMLTextAreaElement>(null)
  const formik = useFormik({
    initialValues: {
      senderParty: '',
      receiverParty: '',
      amount: '',
      remarks: '',
    },
    validationSchema: Yup.object({
      senderParty: Yup.string().required('Sender party is required'),
      receiverParty: Yup.string().required('Receiver party is required'),
      amount: Yup.number()
        .required('Amount is required')
        .typeError('Amount must be a number'),
      remarks: Yup.string(),
    }),
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async (values, { resetForm }) => {
      setLoading(true)
      const selectedSender = senderOptions.find(s => s.value === values.senderParty)
      const selectedReceiver = receiverOptions.find(r => r.value === values.receiverParty)
      const amount = parseFloat(values.amount || '0');
      const formData = {
        date,
        sender_id:selectedSender?.value,
        receiver_id:selectedReceiver?.value,
        amount: values.amount,
        remarks: values.remarks,
      }
      try {
        const response = await submitAutoVatavFormAPI(formData)
        if (response?.status === 200) {
          dispatch(setMessage('success', 'Entry added successfully!'))
          setCharCount(70)
          // Only reset receiver, amount, remarks
          formik.setFieldValue('receiverParty', '')
          formik.setFieldValue('amount', '')
          formik.setFieldValue('remarks', '')
          setReceiverParty(null)
          setBalances(null)
          setReceiverOptions(allParties.filter(option => option.value !== senderParty?.value))
          // Focus receiver select
          receiverSelectRef.current?.focus()
          receiverSelectRef.current?.onMenuOpen()

          // Call getCommissionData after successful submit with amount 0
          if (selectedSender && selectedReceiver) {
            try {
              const commissionPayload = {
                sender_id: selectedSender.value,
                receiver_id: '',
                amount: '0',
                vatav: 1,
              };
              const commissionData = await getCommissionData(commissionPayload);
              setBalances(commissionData);

              setSenderParty((prev: any) => ({
                ...prev,
                current_balance: commissionData.sender_current_balance,
                debit: commissionData.sender_debit,
                net_balance: commissionData.sender_net_balance,
              }));

              setReceiverParty((prev: any) => ({
                ...prev,
                current_balance: commissionData.receiver_current_balance,
                credit: commissionData.receiver_credit,
                net_balance: commissionData.receiver_net_balance,
              }));
            } catch (error) {
              console.error('Error fetching commission data after submit:', error);
            }
          }
        } else {
          dispatch(setMessage('error', response.data.message || 'Failed to add transaction!'))
        }
      } catch (error: any) {
        dispatch(setMessage('error', error?.message || 'Something went wrong'))
      } finally {
        setLoading(false)
      }
    },
  })

  useEffect(() => {
    const fetchParties = async () => {
      try {
        const response = await getParty()
        const parties = response.data.map((x: any) => ({
          label: x.nameCode,
          value: x.partyId,
          ...x,
        }))
        setAllParties(parties)
        setSenderOptions(parties)
        setReceiverOptions(parties)
      } catch (error) {
        console.error('Error fetching parties:', error)
        dispatch(setMessage('error', 'Something went wrong'))
      }
    }
    fetchParties()
    setTimeout(() => {
      senderSelectRef.current?.focus()
      senderSelectRef.current?.onMenuOpen()
    }, 0)
  }, [])

  const handleSenderPartyChange = async (selected: any) => {
    const selectedParty = allParties.find(option => option.value === selected?.value) || null
    setSenderParty(selectedParty)
    formik.setFieldValue('senderParty', selected?.value || '')
    setReceiverOptions(allParties.filter(option => option.value !== selected?.value))
    
    if (selected) {
      receiverSelectRef.current?.focus()
      receiverSelectRef.current?.onMenuOpen()
    }

    // Always fetch commission data when sender party changes
    if (selectedParty) {
      const payload = {
        sender_id: selectedParty.value,
        receiver_id: receiverParty?.value || '',
        amount: formik.values.amount || '0',
        vatav: 1,
      }
      try {
        const partyDetails = await getCommissionData(payload)
        setSenderParty((prev: any) => ({
          ...prev,
          current_balance: partyDetails.sender_current_balance,
          debit: partyDetails.sender_debit,
          net_balance: partyDetails.sender_net_balance,
        }))
        if (receiverParty) {
          setReceiverParty((prev: any) => ({
            ...prev,
            current_balance: partyDetails.receiver_current_balance,
            credit: partyDetails.receiver_credit,
            net_balance: partyDetails.receiver_net_balance,
          }))
        }
        setBalances({
          sender_current_balance: partyDetails.sender_current_balance,
          sender_debit: partyDetails.sender_debit,
          sender_net_balance: partyDetails.sender_net_balance,
          receiver_current_balance: partyDetails.receiver_current_balance,
          receiver_credit: partyDetails.receiver_credit,
          receiver_net_balance: partyDetails.receiver_net_balance,
        })
      } catch (error) {
        console.error('Error fetching commission data:', error)
      }
    }
  }

  const handleReceiverPartyChange = async (selected: any) => {
    const selectedParty = allParties.find(option => option.value === selected?.value) || null
    setReceiverParty(selectedParty)
    formik.setFieldValue('receiverParty', selected?.value || '')
    setSenderOptions(allParties.filter(option => option.value !== selected?.value))
    if (selected) {
      amountInputRef.current?.focus()
    }
    if (senderParty && selectedParty) {
      const payload = {
        sender_id: senderParty.value,
        receiver_id: selectedParty.value,
        amount: formik.values.amount || '0',
        vatav: 1,
      }
      try {
        const partyDetails = await getCommissionData(payload)
        setSenderParty((prev: any) => ({
          ...prev,
          current_balance: partyDetails.sender_current_balance,
          debit: partyDetails.sender_debit,
          net_balance: partyDetails.sender_net_balance,
        }))
        setReceiverParty((prev: any) => ({
          ...prev,
          current_balance: partyDetails.receiver_current_balance,
          credit: partyDetails.receiver_credit,
          net_balance: partyDetails.receiver_net_balance,
        }))
        setBalances({
          sender_current_balance: partyDetails.sender_current_balance,
          sender_debit: partyDetails.sender_debit,
          sender_net_balance: partyDetails.sender_net_balance,
          receiver_current_balance: partyDetails.receiver_current_balance,
          receiver_credit: partyDetails.receiver_credit,
          receiver_net_balance: partyDetails.receiver_net_balance,
        })
      } catch (error) {
        console.error('Error fetching commission data:', error)
      }
    }
  }

  const preventNonNumeric = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!/[0-9-]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') {
      e.preventDefault();
    }
  };

  const handleAmountChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value
    if (value.length > 12) {
      value = value.slice(0, 12)
    }
    if (/^-?\d*$/.test(value)) {
      formik.setFieldValue('amount', value)
    }

    // Clear existing timeout
    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }

    // Set new timeout
    const timeout = setTimeout(async () => {
      if (senderParty && receiverParty) {
        const amount = parseFloat(value || '0');
        const isNegative = amount < 0;
        const payload = {
          sender_id: isNegative ? receiverParty?.value ?? null : senderParty?.value ?? null,
          receiver_id: isNegative ? senderParty?.value ?? null : receiverParty?.value ?? null,
          amount: Math.abs(amount).toString(), // Always positive
          vatav: 1,
        }
        try {
          const commissionData = await getCommissionData(payload)
          setSenderParty((prev: any) => ({
            ...prev,
            current_balance: commissionData.sender_current_balance,
            debit: commissionData.sender_debit,
            credit: commissionData.sender_credit,
            net_balance: commissionData.sender_net_balance,
          }))
          setReceiverParty((prev: any) => ({
            ...prev,
            current_balance: commissionData.receiver_current_balance,
            debit: commissionData.receiver_debit,
            credit: commissionData.receiver_credit,
            net_balance: commissionData.receiver_net_balance,
          }))
          setBalances(commissionData)
        } catch (error) {
          console.error('Error fetching commission data:', error)
        }
      }
    }, 1000);

    setDebounceTimeout(timeout);
  };

  const handleCancel = () => {
    formik.resetForm()
    formik.setTouched({}, false)
    setSenderParty(null)
    setReceiverParty(null)
    setBalances(null)
    setCharCount(70)
    setSenderOptions(allParties)
    setReceiverOptions(allParties)
    senderSelectRef.current?.focus()
    senderSelectRef.current?.onMenuOpen()
  }

  // Helper to safely format amounts
  const safeFormatAmount = (val: any) => {
    if (val === undefined || val === null || isNaN(Number(val))) return '-';
    return constants.formatAmount(val);
  };

  const amount = parseFloat(formik.values.amount || '0');
  const isNegative = amount < 0;

  const displaySender = isNegative ? receiverParty : senderParty;
  const displayReceiver = isNegative ? senderParty : receiverParty;

  const displaySenderLabel = isNegative ? 'Credit' : 'Debit';
  const displayReceiverLabel = isNegative ? 'Debit' : 'Credit';

  const displaySenderBalance = safeFormatAmount(displaySender?.current_balance);
  const displaySenderDebit = safeFormatAmount(isNegative ? displaySender?.credit : displaySender?.debit);
  const displaySenderNet = safeFormatAmount(displaySender?.net_balance);

  const displayReceiverBalance = safeFormatAmount(displayReceiver?.current_balance);
  const displayReceiverCredit = safeFormatAmount(isNegative ? displayReceiver?.debit : displayReceiver?.credit);
  const displayReceiverNet = safeFormatAmount(displayReceiver?.net_balance);

  const customSelectStyles = {
    control: (provided: any) => ({
      ...provided,
      borderColor: '#E4E6EF',
      borderWidth: '2px',
      minHeight: 'calc(1.5em + .75rem + 16px)',
      borderRadius: '0.475rem',
      cursor: 'pointer',
      boxShadow: 'none',
      '&:hover': {
        borderColor: '#cdcdde',
      },
    }),
    valueContainer: (provided: any) => ({
      ...provided,
      lineHeight: '1.5',
    }),
    input: (provided: any) => ({
      ...provided,
      margin: '0',
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
    indicatorsContainer: (provided: any) => ({
      ...provided,
      height: 'calc(1.5em + .75rem + 16px)',
    }),
    option: (provided: any) => ({
      ...provided,
      cursor: 'pointer',
    }),
  }

  return (
    <div className='card'>
      <div className='card-header min-h-auto py-5'>
        <h2 className='fw-bolder'>Normal Entry</h2>
        <div
                                      className="btn btn-icon btn-shadow-lg  bg-primary text-white position-absolute start-100 translate-middle d-flex align-items-center justify-content-center border border-light"
                                      style={{ zIndex: 99, top: "33px", marginLeft: "-34px" ,width: "34px",
                                        height: "32px"}}
                                      role="button"
                                      aria-label="Go back to previous page"
                                      tabIndex={0}
                                      data-tooltip="Back"
                                      onClick={() => navigate(-1)}
                                    >
                                      <KTSVG path="/media/icons/duotune/arrows/arr025.svg" className="svg-icon-4 text-white" />
                                    </div>
      </div>
      <div className='card-body'>
        <Form onSubmit={formik.handleSubmit} noValidate>
          <Row className='mb-5'>
            <Col md={6}>
              <Form.Group controlId='date'>
                <Form.Label>Date</Form.Label>
                <DatePicker
                  selected={today}
                  className="form-control"
                  dateFormat="dd-MM-yyyy"
                  disabled
                />
              </Form.Group>
            </Col>
            <Col md={6}></Col>
          </Row>

          <Row className='mb-5 align-items-center'>
            <Col md={6}>
              <Form.Group controlId='senderParty'>
                <Form.Label>Sender Party</Form.Label>
                <Select
                  ref={senderSelectRef}
                  id='sender-party'
                  onChange={handleSenderPartyChange}
                  options={senderOptions}
                  placeholder="Sender Party"
                  value={senderOptions.find(option => option.value === formik.values.senderParty) || null}
                  styles={customSelectStyles}
                  filterOption={(option, inputValue) => {
                    if (!inputValue) return true;
                    return option.label.toLowerCase().startsWith(inputValue.toLowerCase());
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Tab' && !formik.values.senderParty) {
                      e.preventDefault();
                      receiverSelectRef.current?.focus();
                      receiverSelectRef.current?.onMenuOpen()

                    }
                  }}
                />
                {formik.touched.senderParty && formik.errors.senderParty && (
                  <div className='fv-plugins-message-container text-danger fs-8'>
                    <span role='alert'>{formik.errors.senderParty}</span>
                  </div>
                )}
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Label className='fs-8'>Current Balance</Form.Label>
              <div>{displaySenderBalance}</div>
            </Col>
            <Col md={2}>
              <Form.Label className='fs-8'>{displaySenderLabel}</Form.Label>
              <div>{displaySenderDebit}</div>
            </Col>
            <Col md={2}>
              <Form.Label className='fs-8'>Net Balance</Form.Label>
              <div>{displaySenderNet}</div>
            </Col>
          </Row>

          <Row className='mb-5 align-items-center'>
            <Col md={6}>
              <Row>
                <Col md={6}>
                  <Form.Group controlId='receiverParty'>
                    <Form.Label>Receiver Party</Form.Label>
                    <Select
                      ref={receiverSelectRef}
                      id='receiver-party'
                      onChange={handleReceiverPartyChange}
                      options={receiverOptions}
                      placeholder="Receiver Party"
                      value={receiverOptions.find(option => option.value === formik.values.receiverParty) || null}
                      styles={customSelectStyles}
                      filterOption={(option, inputValue) => {
                        if (!inputValue) return true;
                        return option.label.toLowerCase().startsWith(inputValue.toLowerCase());
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Tab' && !formik.values.receiverParty) {
                          e.preventDefault();
                          amountInputRef.current?.focus();
                       
    
                        }
                      }}
                    />
                    {formik.touched.receiverParty && formik.errors.receiverParty && (
                      <div className='fv-plugins-message-container text-danger fs-8'>
                        <span role='alert'>{formik.errors.receiverParty}</span>
                      </div>
                    )}
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId='amount'>
                    <Form.Label>Amount</Form.Label>
                    <Form.Control
                      type='text'
                      name='amount'
                      inputMode='numeric'
                      pattern="[0-9-]*"
                      maxLength={12}
                      placeholder="Amount"
                      value={formik.values.amount}
                      onChange={handleAmountChange}
                      onPaste={(e: React.ClipboardEvent<HTMLInputElement>) => {
                        e.preventDefault()
                        const pastedValue = e.clipboardData.getData('text')
                        let cleanValue = pastedValue.replace(/[^0-9-]/g, '')
                        if (cleanValue.length > 12) {
                          cleanValue = cleanValue.slice(0, 12)
                        }
                        formik.setFieldValue('amount', cleanValue)
                      }}
                      ref={amountInputRef}
                      onKeyPress={preventNonNumeric}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault()
                          remarkSelectRef.current?.focus()
                        }
                      }}
                    />
                    {formik.touched.amount && formik.errors.amount && (
                      <div className='fv-plugins-message-container text-danger fs-8'>
                        <span role='alert'>{formik.errors.amount}</span>
                      </div>
                    )}
                  </Form.Group>
                </Col>
              </Row>
            </Col>
            <Col md={2}>
              <Form.Label className='fs-8'>Current Balance</Form.Label>
              <div>{displayReceiverBalance}</div>
            </Col>
            <Col md={2}>
              <Form.Label className='fs-8'>{displayReceiverLabel}</Form.Label>
              <div>{displayReceiverCredit}</div>
            </Col>
            <Col md={2}>
              <Form.Label className='fs-8'>Net Balance</Form.Label>
              <div>{displayReceiverNet}</div>
            </Col>
          </Row>

          <Row className='mb-5'>
            <Col md={12}>
              <Form.Group controlId='remarks'>
                <Form.Label>Remarks</Form.Label>
                <Form.Control
                  as='textarea'
                  rows={3}
                  name='remarks'
                  placeholder="Remarks"
                  maxLength={70}
                  style={{ maxHeight: '190px' }}
                  value={formik.values.remarks}
                  onChange={(e) => {
                    const inputValue = e.target.value
                    formik.setFieldValue('remarks', inputValue.trimStart())
                    setCharCount(70 - inputValue.length)
                  }}
                  ref={remarkSelectRef}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey && !loading) {
                      e.preventDefault()
                      formik.handleSubmit()
                    }
                  }}
                />
                <div className='text-end'>
                  <small className={charCount <= 10 ? 'text-danger' : ''}>
                    Remaining characters: {charCount}
                  </small>
                </div>
              </Form.Group>
            </Col>
          </Row>

          <Row className='mb-5'>
            <Col md={12} className='d-flex justify-content-end'>
              <Button
                variant='primary'
                type='submit'
                className='px-20 mx-w-100'
                disabled={loading}
              >
                {loading ? 'Please Wait...' : 'Submit'}
              </Button>
              <Button
                variant='secondary'
                type='button'
                className='ms-3 px-20 mx-w-100'
                onClick={handleCancel}
              >
                Cancel
              </Button>
            </Col>
          </Row>
        </Form>
      </div>
    </div>
  )
}

export default NormalEntry
